import { useState, useCallback, useRef } from 'react';
import { IImageThumbnail } from '../../../Entities';

type UseImagePinningParams = {
    images: IImageThumbnail[];
};

type UseImagePinningResult = {
    isPinning: boolean;
    pinDialogOpen: boolean;
    pinImage: IImageThumbnail | null;
    startPinning: () => void;
    cancelPinning: () => void;
    openPinDialog: (imageId: number, onCancel?: () => void) => void;
    closePinDialog: () => void;
    handlePinCreated: () => void;
};

export function useImagePinning({ images }: UseImagePinningParams): UseImagePinningResult {
    const [isPinning, setIsPinning] = useState(false);
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [pinImage, setPinImage] = useState<IImageThumbnail | null>(null);
    const onCancelRef = useRef<(() => void) | null>(null);

    const startPinning = useCallback(() => {
        setIsPinning(true);
    }, []);

    const cancelPinning = useCallback(() => {
        setIsPinning(false);
    }, []);

    const openPinDialog = useCallback(
        (imageId: number, onCancel?: () => void) => {
            const selectedImage = images.find((img) => img.id === imageId);
            if (!selectedImage) {
                return;
            }
            onCancelRef.current = onCancel || null;
            setPinImage(selectedImage);
            setPinDialogOpen(true);
        },
        [images]
    );

    const closePinDialog = useCallback(() => {
        const onCancel = onCancelRef.current;
        onCancelRef.current = null;
        setPinDialogOpen(false);
        if (onCancel) {
            onCancel();
        }
    }, []);

    const handlePinCreated = useCallback(() => {
        onCancelRef.current = null;
        setPinDialogOpen(false);
        setIsPinning(false);
    }, []);

    return {
        isPinning,
        pinDialogOpen,
        pinImage,
        startPinning,
        cancelPinning,
        openPinDialog,
        closePinDialog,
        handlePinCreated
    };
}
